// ===========================================
// CARD COMPONENT - Premium Glass UI
// ===========================================

import React from 'react';

interface CardProps {
  children: React.ReactNode;
  variant?: 'default' | 'glass' | 'gradient' | 'outlined';
  hover?: boolean;
  glow?: boolean;
  padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  onClick?: () => void;
}

const Card: React.FC<CardProps> = ({
  children,
  variant = 'default',
  hover = false,
  glow = false,
  padding = 'md',
  className = '',
  onClick,
}) => {
  const baseStyles = `
    rounded-2xl transition-all duration-300
    ${onClick ? 'cursor-pointer' : ''}
    ${hover ? 'hover:-translate-y-1 hover:shadow-xl' : ''}
    ${glow ? 'hover:shadow-2xl hover:shadow-tripnovac-500/20' : ''}
  `;

  const variants = {
    default: `
      bg-white shadow-lg shadow-gray-200/50
      border border-gray-100
    `,
    glass: `
      glass-card
      backdrop-blur-xl bg-white/70
      border border-white/30
      shadow-xl shadow-tripnovac-500/10
    `,
    gradient: `
      bg-gradient-to-br from-tripnovac-500 via-violet-500 to-lilac-500
      text-white shadow-xl shadow-tripnovac-500/30
    `,
    outlined: `
      bg-transparent
      border-2 border-gray-200
      hover:border-tripnovac-300
    `,
  };

  const paddings = {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
    xl: 'p-10',
  };

  return (
    <div
      className={`
        ${baseStyles}
        ${variants[variant]}
        ${paddings[padding]}
        ${className}
      `}
      onClick={onClick}
    >
      {children}
    </div>
  );
};

// Card Header Component
interface CardHeaderProps {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  className?: string;
}

export const CardHeader: React.FC<CardHeaderProps> = ({
  title,
  subtitle,
  action,
  className = '',
}) => (
  <div className={`flex items-center justify-between mb-6 ${className}`}>
    <div>
      <h3 className="text-lg font-bold text-gray-900">{title}</h3>
      {subtitle && (
        <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>
      )}
    </div>
    {action && <div>{action}</div>}
  </div>
);

// Card Content Component
interface CardContentProps {
  children: React.ReactNode;
  className?: string;
}

export const CardContent: React.FC<CardContentProps> = ({
  children,
  className = '',
}) => (
  <div className={className}>{children}</div>
);

// Card Footer Component
interface CardFooterProps {
  children: React.ReactNode;
  className?: string;
}

export const CardFooter: React.FC<CardFooterProps> = ({
  children,
  className = '',
}) => (
  <div className={`mt-6 pt-6 border-t border-gray-100 ${className}`}>
    {children}
  </div>
);

export default Card;
